import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { getErrorMessage, getFieldErrors } from '@/lib/errors';
import { serversApi } from '@/lib/servers-api';
import { cn } from '@/lib/utils';
import type { SshCredential } from '@/types/api';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  credential: SshCredential | null;
}

type AuthType = 'private_key' | 'password';

const EMPTY = {
  name: '',
  auth_type: 'private_key' as AuthType,
  username: 'root',
  secret: '',
  passphrase: '',
  sudo_password: '',
};

/**
 * Add / edit a reusable SSH credential.
 *
 * ## Secrets go in, and never come back out
 *
 * The key, its passphrase and the sudo password are write-only. Editing shows that a sudo password is
 * stored — never the password — and offers the two things a person can do about it: type a new one to
 * replace it, or forget it.
 */

export function CredentialFormDialog({ open, onOpenChange, credential }: Props) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [forgetSudo, setForgetSudo] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;

    setFieldErrors({});
    setForgetSudo(false);
    setForm(
      credential
        ? {
            name: credential.name,
            auth_type: credential.auth_type,
            username: credential.username,
            // Never pre-filled: the server does not return secrets, and an empty value
            // on update means "keep the existing one".
            secret: '',
            passphrase: '',
            sudo_password: '',
          }
        : EMPTY,
    );
  }, [open, credential]);

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name: form.name.trim(),
        auth_type: form.auth_type,
        username: form.username.trim(),
        secret: form.secret,
        ...(form.passphrase ? { passphrase: form.passphrase } : {}),
        ...(form.sudo_password !== ''
          ? { sudo_password: form.sudo_password }
          : forgetSudo
            ? { forget_sudo_password: true }
            : {}),
      };

      return credential
        ? serversApi.updateCredential(credential.id, payload)
        : serversApi.createCredential(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['server-inventory'] });
      onOpenChange(false);
    },
    onError: (error) => setFieldErrors(getFieldErrors(error)),
  });

  const isKey = form.auth_type === 'private_key';
  const set = (key: keyof typeof EMPTY) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{credential ? 'Edit credential' : 'Add an SSH credential'}</DialogTitle>
          <DialogDescription>
            Stored encrypted and reusable across servers. It is never shown again after saving.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            setFieldErrors({});
            save.mutate();
          }}
        >
          {save.error && Object.keys(fieldErrors).length === 0 && (
            <Alert variant="danger">{getErrorMessage(save.error)}</Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="cf-name">Name</Label>
            <Input
              id="cf-name"
              value={form.name}
              onChange={(e) => set('name')(e.target.value)}
              required
              autoFocus
            />
            {fieldErrors.name ? (
              <p className="text-xs text-danger">{fieldErrors.name}</p>
            ) : (
              <p className="text-xs text-muted-foreground">How this credential is listed when you choose one for a server.</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Authentication method</Label>
            <div className="grid grid-cols-2 gap-2">
              <AuthChoice
                selected={isKey}
                onSelect={() => setForm((c) => ({ ...c, auth_type: 'private_key' }))}
                title="SSH key"
                subtitle="Recommended"
              />
              <AuthChoice
                selected={!isKey}
                onSelect={() => setForm((c) => ({ ...c, auth_type: 'password' }))}
                title="Password"
                subtitle="Less secure"
              />
            </div>
            {fieldErrors.auth_type && <p className="text-xs text-danger">{fieldErrors.auth_type}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="cf-user">SSH username</Label>
            <Input
              id="cf-user"
              value={form.username}
              onChange={(e) => set('username')(e.target.value)}
              required
            />
            {fieldErrors.username ? (
              <p className="text-xs text-danger">{fieldErrors.username}</p>
            ) : (
              <p className="text-xs text-muted-foreground">root, or an account that can use sudo.</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="cf-secret">{isKey ? 'Private key' : 'Password'}</Label>
            {isKey ? (
              <Textarea
                id="cf-secret"
                className="min-h-40 font-mono text-xs"
                value={form.secret}
                onChange={(e) => set('secret')(e.target.value)}
                spellCheck={false}
                autoComplete="off"
                required={credential === null}
              />
            ) : (
              <Input
                id="cf-secret"
                type="password"
                value={form.secret}
                onChange={(e) => set('secret')(e.target.value)}
                autoComplete="new-password"
                required={credential === null}
              />
            )}
            {fieldErrors.secret ? (
              <p className="text-xs text-danger">{fieldErrors.secret}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                {credential
                  ? 'Leave blank to keep the existing secret.'
                  : isKey
                    ? 'Paste the private half of the key pair. The public half is derived for you.'
                    : 'Key-based authentication is strongly preferred where the server supports it.'}
              </p>
            )}
          </div>

          {isKey && (
            <div className="space-y-2">
              <Label htmlFor="cf-pass">
                Key passphrase <span className="font-normal text-muted-foreground">(only if the key is encrypted)</span>
              </Label>
              <Input
                id="cf-pass"
                type="password"
                value={form.passphrase}
                onChange={(e) => set('passphrase')(e.target.value)}
                autoComplete="new-password"
              />
              {fieldErrors.passphrase && (
                <p className="text-xs text-danger">{fieldErrors.passphrase}</p>
              )}
            </div>
          )}

          {/* root never needs sudo, so the field waits until the account is not root, unless a
              password is already stored and has to stay forgettable. */}
          {(form.username.trim() !== 'root' || credential?.stores_sudo_password === true) && (
            <div className="space-y-2">
              <Label htmlFor="cf-sudo">
                Sudo password <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="cf-sudo"
                type="password"
                value={form.sudo_password}
                onChange={(e) => {
                  set('sudo_password')(e.target.value);
                  if (e.target.value !== '') setForgetSudo(false);
                }}
                autoComplete="new-password"
                disabled={forgetSudo}
              />
              {fieldErrors.sudo_password ? (
                <p className="text-xs text-danger">{fieldErrors.sudo_password}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {credential?.stores_sudo_password ? 'A sudo password is stored. Type a new one to replace it. ' : ''}
                  {isKey
                    ? 'Used only when sudo on a server asks this account for its password. Stored encrypted and never shown again.'
                    : 'When sudo asks for a password, the SSH password is offered unless a different sudo password is stored here.'}
                </p>
              )}
              {credential?.stores_sudo_password && (
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={forgetSudo}
                    onChange={(e) => {
                      setForgetSudo(e.target.checked);
                      if (e.target.checked) set('sudo_password')('');
                    }}
                    className="size-4 accent-primary"
                  />
                  Forget the stored sudo password
                </label>
              )}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending && <Loader2 className="animate-spin" />}
              {credential ? 'Save changes' : 'Add credential'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AuthChoice({
  selected,
  onSelect,
  title,
  subtitle,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  subtitle: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'rounded-md border px-3 py-2.5 text-left transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected
          ? 'border-primary/60 bg-primary/15'
          : 'border-border bg-muted/20 hover:border-primary/40',
      )}
    >
      <span className="block text-sm font-medium">{title}</span>
      <span className="block text-xs text-muted-foreground">{subtitle}</span>
    </button>
  );
}
